/**
 * Leviathan Web - Suppliers Page
 * Quasar Prime LLC
 * Aggressive Tactical Industrial Design
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { GeometricPanel } from '@components/ui/GeometricPanel';
import { API_URL } from '@/config/api';
import { fullName } from '@/utils/personName';

interface Supplier {
  id: string;
  supplierNumber: string;
  name: string;
  displayName: string | null;
  primaryContactFirstName: string | null;
  primaryContactLastName: string | null;
  email: string | null;
  phone: string | null;
  city: string | null;
  state: string | null;
  paymentTerms: number | null;
  creditLimit: number | null;
  isActive: boolean;
}

interface Stats {
  total: number;
  active: number;
  inactive: number;
}

interface StatCardProps {
  label: string;
  value: number;
  status?: 'operational' | 'warning' | 'critical';
}

/**
 * Compact stat card for supplier metrics
 */
function StatCard({ label, value, status }: StatCardProps) {
  const statusColor =
    status === 'operational'
      ? 'bg-success-green'
      : status === 'warning'
        ? 'bg-warning-amber'
        : status === 'critical'
          ? 'bg-error-red'
          : 'bg-steel';

  return (
    <div className="relative stat-card">
      <div className="absolute top-0 left-0 w-2 h-2 border-l border-t border-amber" />
      <div className="absolute top-0 right-0 w-2 h-2 border-r border-t border-amber" />
      <div className="absolute bottom-0 left-0 w-2 h-2 border-l border-b border-amber" />
      <div className="absolute bottom-0 right-0 w-2 h-2 border-r border-b border-amber" />

      <div className="bg-panel-dark border border-panel-border p-4 clip-path-octagon relative">
        {status && (
          <div className="absolute top-3 right-3">
            <div
              className={`w-2 h-2 ${statusColor} rounded-full animate-pulse`}
              style={{ boxShadow: '0 0 8px currentColor' }}
            />
          </div>
        )}
        <div className="text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
          {label}
        </div>
        <div
          className="text-3xl font-mono font-bold text-amber"
          style={{
            textShadow: '0 0 10px rgba(255, 149, 0, 0.6)',
          }}
        >
          {value}
        </div>
      </div>
    </div>
  );
}

const SuppliersPage = () => {
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('');

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchSuppliers();
  }, [search, activeFilter]);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/suppliers/stats`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchSuppliers = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (activeFilter) params.append('isActive', activeFilter);

      const response = await fetch(`${API_URL}/api/suppliers?${params}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSuppliers(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch suppliers:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setActiveFilter('');
  };

  return (
    <div className="space-y-6 relative">
      {/* Header with geometric frame */}
      <div className="relative">
        <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber shadow-[0_0_8px_rgba(255,149,0,0.6)]" />
        <div className="border-l-4 border-amber pl-6 relative">
          <div className="flex items-center justify-between">
            <div>
              <h1
                className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
                style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
              >
                Suppliers
              </h1>
              <p className="text-text-secondary text-sm font-mono mt-1">
                Fuel Vendors & Terminal Management
              </p>
            </div>
            <Link
              to="/suppliers/new"
              className="bg-amber hover:bg-amber/90 text-background font-display font-bold py-3 px-6 transition-all uppercase tracking-wider relative group"
              style={{ boxShadow: '0 0 20px rgba(255, 149, 0, 0.4)' }}
            >
              <span className="relative z-10">+ New Supplier</span>
              <div
                className="absolute inset-0 bg-gradient-to-r from-amber to-steel opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ mixBlendMode: 'overlay' }}
              />
            </Link>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-3 gap-4">
          <StatCard label="Total" value={stats.total} />
          <StatCard label="Active" value={stats.active} status="operational" />
          <StatCard label="Inactive" value={stats.inactive} status="warning" />
        </div>
      )}

      {/* Filters Panel */}
      <GeometricPanel>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
              Search
            </label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name, number, email..."
              className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm transition-all placeholder:text-text-tertiary"
            />
          </div>
          <div>
            <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
              Status
            </label>
            <select
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value)}
              className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm transition-all"
            >
              <option value="">All</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={clearFilters}
              className="w-full bg-panel-border hover:bg-steel/20 text-text-primary font-mono py-2 px-4 transition-all uppercase text-xs tracking-wider border border-panel-border hover:border-steel"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </GeometricPanel>

      {/* Supplier Table */}
      <div className="relative stat-card">
        <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
        <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
        <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
        <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

        <div className="bg-panel-dark border border-panel-border clip-path-octagon overflow-hidden">
          {isLoading ? (
            <div className="p-12 text-center">
              <div className="text-text-tertiary font-mono animate-pulse">
                &gt; Loading suppliers...
              </div>
            </div>
          ) : suppliers.length === 0 ? (
            <div className="p-12 text-center">
              <div className="text-text-tertiary font-mono text-lg mb-2">
                &gt; No suppliers found
              </div>
              <p className="text-text-tertiary font-mono text-sm">
                {search || activeFilter
                  ? 'Try adjusting your search filters'
                  : 'Create your first supplier to get started'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-background border-b border-panel-border">
                  <tr>
                    <th className="text-left px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Supplier
                    </th>
                    <th className="text-left px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Contact
                    </th>
                    <th className="text-left px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Location
                    </th>
                    <th className="text-left px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Terms
                    </th>
                    <th className="text-left px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Credit Limit
                    </th>
                    <th className="text-center px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Status
                    </th>
                    <th className="text-center px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {suppliers.map((supplier) => (
                    <tr
                      key={supplier.id}
                      className="border-b border-panel-border hover:bg-amber/5 transition-colors cursor-pointer"
                      onClick={() => navigate(`/suppliers/${supplier.id}`)}
                    >
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-amber font-mono font-semibold">
                            {supplier.name}
                          </div>
                          <div className="text-text-tertiary font-mono text-xs">
                            {supplier.supplierNumber}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          {fullName({
                            firstName: supplier.primaryContactFirstName,
                            lastName: supplier.primaryContactLastName,
                          }) && (
                            <div className="text-text-primary font-mono text-sm">
                              {fullName({
                                firstName: supplier.primaryContactFirstName,
                                lastName: supplier.primaryContactLastName,
                              })}
                            </div>
                          )}
                          {supplier.phone && (
                            <div className="text-text-tertiary font-mono text-xs">
                              {supplier.phone}
                            </div>
                          )}
                          {!fullName({
                            firstName: supplier.primaryContactFirstName,
                            lastName: supplier.primaryContactLastName,
                          }) &&
                            !supplier.phone && (
                            <span className="text-text-tertiary font-mono text-sm">-</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-text-secondary font-mono text-sm">
                          {supplier.city && supplier.state
                            ? `${supplier.city}, ${supplier.state}`
                            : '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-text-secondary font-mono text-sm">
                          {supplier.paymentTerms ? `NET ${supplier.paymentTerms}` : '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div
                          className="text-amber font-mono font-semibold"
                          style={{ textShadow: '0 0 6px rgba(255, 149, 0, 0.4)' }}
                        >
                          {supplier.creditLimit
                            ? `$${Number(supplier.creditLimit).toLocaleString()}`
                            : '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span
                          className={`${supplier.isActive ? 'text-success-green' : 'text-text-tertiary'} font-mono text-xs uppercase tracking-wider font-semibold`}
                          style={{ textShadow: '0 0 6px currentColor' }}
                        >
                          {supplier.isActive ? 'ACTIVE' : 'INACTIVE'}
                        </span>
                      </td>
                      <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-center space-x-2">
                          <button
                            onClick={() => navigate(`/receipts/new?supplierId=${supplier.id}`)}
                            className="text-amber hover:text-amber/80 font-mono text-sm"
                            title="New Receipt"
                          >
                            📦
                          </button>
                          <button
                            onClick={() => navigate(`/ap/new?supplierId=${supplier.id}`)}
                            className="text-steel hover:text-steel/80 font-mono text-sm"
                            title="New Bill"
                          >
                            📄
                          </button>
                          <button
                            onClick={() => navigate(`/suppliers/${supplier.id}`)}
                            className="text-steel hover:text-amber font-mono text-sm"
                            title="View"
                          >
                            👁️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SuppliersPage;
